import BookOutlined from '@mui/icons-material/BookOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useAccessEvents from 'v2/hooks/useAccessEvents/useAccessEvents';
import useAccessEventsDataForFilters from 'v2/hooks/useAccessEvents/useAccessEventsDataForFilters';
import ExportModal from './components/ExportModal';
import PreparingExportModal from './components/PreparingExportModal';
import TableWithData from './components/TableWithData';
import { useEventsSearchAndFilters } from './useEventsSearchAndFilters';

const Overview: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);

  const { doors, doorsLoading, visitors, visitorsLoading } = useAccessEventsDataForFilters({
    handleFetchError: () => showSnackbar(translation.access_events_error_fetching, { severity: 'error' })
  });

  const { sections, chips, apiFilter, isAnyFilterEnabled, clearAllFilters } = useEventsSearchAndFilters({
    visitors,
    doors
  });

  useEffect(() => {
    setPage(0);
  }, [apiFilter]);

  const { accessEvents, rowCount, loading } = useAccessEvents({
    handleFetchError: (message) => showSnackbar(message, { severity: 'error' }),
    translation,
    page,
    filter: apiFilter
  });

  const handleExport = () => {
    if (isAnyFilterEnabled) {
      openModal(<ExportModal filter={apiFilter} />);
    } else {
      openModal(<PreparingExportModal filter={null} />);
    }
  };

  const breadcrumb = { title: translation.access_events, crumbs: [] };
  const hasData = loading || accessEvents.length > 0 || isAnyFilterEnabled;
  const hasResults = accessEvents.length > 0;

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack>
            <Typography variant="h4">{translation.access_events}</Typography>
            <Typography variant="body2" color="textSecondary">
              {translation.events_last_30_days_description}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction={isMobile ? 'column-reverse' : 'row'}
          spacing={2}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'flex-start'}
        >
          <SearchAndFilters sections={sections} chips={chips} onClearAll={clearAllFilters} />
          <Button id="export-events-button" variant="outlined" startIcon={<DownloadOutlined />} onClick={handleExport}>
            {translation.export}
          </Button>
        </Stack>
        <OverviewContent
          loading={loading}
          hasData={hasData}
          hasResults={hasResults}
          table={
            <TableWithData
              rows={accessEvents}
              loading={loading}
              rowCount={rowCount}
              page={page}
              onPageChange={setPage}
            />
          }
          noResults={
            <EmptyStateOverview
              icon={BookOutlined}
              title={translation.no_events_found}
              content={translation.no_results_description}
            />
          }
          noData={
            <EmptyStateOverview
              icon={BookOutlined}
              title={translation.no_events}
              content={translation.no_events_description}
            />
          }
        />
      </Stack>
    </>
  );
};

export default Overview;
